import { getPricingConfig, getCachedPricingConfig, type PricingConfig } from '../config/pricingConfig';

export interface PricingInputs {
  msrp: number;
  age: number; // Vehicle age in years
  mileage: number; // Mileage in km
  downPayment: number;
  apr?: number; // Optional, defaults to config value
  markupCap?: number; // Optional, defaults to config value
  sourceScore?: number; // Optional, defaults to config value
  safetyMargin?: number; // Optional, defaults to config value
}

export interface PricingResult {
  monthly: number;
  term: number;
  salePrice: number;
  markup: number;
  minScore: number;
  reasoning: string;
  error?: string;
}

/**
 * Get age tier based on vehicle age
 */
const getAgeTier = (age: number): number => {
  if (age <= 3) return 1;
  if (age <= 6) return 2;
  if (age <= 10) return 3;
  return 4;
};

/**
 * Get mileage tier based on vehicle mileage
 */
const getMileageTier = (km: number): number => {
  if (km <= 60000) return 1;
  if (km <= 130000) return 2;
  if (km <= 180000) return 3;
  return 4;
};

/**
 * Convert tier to term duration in months
 */
const tierToTerm = (tier: number): number => {
  switch (tier) {
    case 1: return 60;
    case 2: return 48;
    case 3: return 36;
    default: return 24;
  }
};

/**
 * Round value to nearest 10
 */
const roundClean = (val: number): number => {
  return Math.round(val / 10) * 10;
};

/**
 * Calculate vehicle age from year
 */
export const calculateVehicleAge = (year: number): number => {
  const currentYear = new Date().getFullYear();
  return currentYear - year;
};

/**
 * Extract year from car name (e.g., "2020 Toyota Camry" -> 2020)
 */
export const extractYearFromCarName = (carName: string): number | null => {
  if (!carName) return null;
  const match = carName.match(/^(\d{4})/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Calculate pricing for a vehicle listing
 * Based on the halal auto pricing algorithm
 */
export const calculatePricing = async (inputs: PricingInputs): Promise<PricingResult> => {
  // Get current pricing config from API (with localStorage fallback)
  let config: PricingConfig;
  try {
    config = await getPricingConfig();
  } catch (error) {
    console.warn('Failed to fetch pricing config from API, using cached:', error);
    config = getCachedPricingConfig();
  }
  
  // Use provided values or fall back to config defaults
  const apr = inputs.apr ?? config.apr;
  const cap = inputs.markupCap ?? config.markupCap;
  const sourceScore = inputs.sourceScore ?? config.sourceScore;
  const safetyMargin = inputs.safetyMargin ?? config.safetyMargin;

  // Validate inputs
  if (!inputs.msrp || inputs.msrp <= 0) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'MSRP must be greater than 0'
    };
  }

  if (inputs.age < 0) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Vehicle age cannot be negative'
    };
  }

  if (inputs.mileage < 0) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Mileage cannot be negative'
    };
  }

  if (inputs.downPayment < 0 || inputs.downPayment >= inputs.msrp) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Down payment must be between 0 and MSRP'
    };
  }

  // Calculate tiers
  const ageTier = getAgeTier(inputs.age);
  const mileageTier = getMileageTier(inputs.mileage);
  const worstTier = Math.max(ageTier, mileageTier);
  let term = tierToTerm(worstTier);

  // Calculate monthly payment
  const monthlyRate = apr / 100 / 12;
  const P_finance = inputs.msrp - inputs.downPayment;
  let monthly = monthlyRate * P_finance / (1 - Math.pow(1 + monthlyRate, -term));
  monthly = roundClean(monthly);
  let salePrice = monthly * term + inputs.downPayment;
  let markup = (salePrice - inputs.msrp) / inputs.msrp;

  // Reduce term if markup exceeds cap
  while (markup > cap && term >= 24) {
    term -= 12;
    monthly = monthlyRate * P_finance / (1 - Math.pow(1 + monthlyRate, -term));
    monthly = roundClean(monthly);
    salePrice = monthly * term + inputs.downPayment;
    markup = (salePrice - inputs.msrp) / inputs.msrp;
  }

  // Check if markup still exceeds cap
  if (markup > cap) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Markup exceeds cap even at shortest term. Please adjust MSRP or down payment.'
    };
  }

  // Generate reasoning
  const reasoning = `The vehicle falls into tier ${ageTier} on the age matrix and tier ${mileageTier} on the mileage matrix, so the worst case (tier ${worstTier}) caps the financing at ${tierToTerm(worstTier)} months. Given a markup cap of ${(cap * 100).toFixed(0)}% and an APR of ${apr}%, only the ${term}-month term satisfies both conditions.`;

  return {
    monthly,
    term,
    salePrice: Math.round(salePrice * 100) / 100,
    markup: markup * 100,
    minScore: sourceScore + safetyMargin,
    reasoning
  };
};

